import type { ApprovalTraceLog, ApprovalTraceTask } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatDisplayTime } from '@hq/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { hq } from '../api/client';
import { LEVEL_LABELS } from '../config';
import { ellipsisCell, sumWidth } from '@hq/ui-kit';

const STATUS_META: Record<string, { color: string; text: string }> = {
  PENDING: { color: 'processing', text: '待处理' },
  DONE: { color: 'green', text: '已处理' },
  TRANSFERRED: { color: 'orange', text: '已转办' },
  SKIPPED: { color: 'default', text: '已跳过' }
};

/** 审批单/审批实例状态（`wl_approval_*`）：日志的「状态变更」列不得直接渲染英文码。 */
const APPROVAL_STATUS_TEXT: Record<string, string> = {
  DRAFT: '草稿',
  PENDING: '待审批',
  APPROVING: '审批中',
  APPROVED: '已通过',
  REJECTED: '已驳回',
  CANCELED: '已取消',
  TERMINATED: '已终止',
  CLOSED: '已关闭'
};

const statusText = (value?: string | null): string =>
  value ? APPROVAL_STATUS_TEXT[value] ?? value : '-';

const ACTION_TEXT: Record<string, string> = {
  APPROVE: '同意',
  REJECT: '驳回',
  RETURN: '退回',
  TRANSFER: '转办',
  ADD_SIGN: '加签',
  REMIND: '催办',
  TERMINATE: '终止',
  SUBMIT: '提交',
  CC: '抄送',
  ESCALATE: '上抛',
  AUTO_PASS: '自动通过',
  AUTO_REJECT: '自动驳回',
  FINISH: '结束',
  SKIP: '跳过'
};

interface Props {
  instanceId: string;
  /** 干预动作成功后回调（父级刷新业务详情）。 */
  onChanged?: () => void | Promise<void>;
}

/**
 * 审批流转与干预：轨迹（任务 + 日志）+ 按 capabilities 显示 催办/转办/加签/退回/终止。
 * 只读接口需要 VIEW_LOG 授权，参与人（发起人/处理人）天然可见。
 */
export default function ApprovalTracePanel({ instanceId, onChanged }: Props) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  const [transferOpen, setTransferOpen] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [terminateOpen, setTerminateOpen] = useState(false);
  const [userKeyword, setUserKeyword] = useState('');

  const [transferForm] = Form.useForm<{ toUserId: string; opinion?: string }>();
  const [signForm] = Form.useForm<{ userId: string; opinion?: string }>();
  const [rejectForm] = Form.useForm<{ targetNodeCode: string; opinion?: string }>();
  const [terminateForm] = Form.useForm<{ reason?: string }>();

  const trace = useQuery({
    queryKey: ['approval', 'trace', instanceId],
    queryFn: () => hq.approval.trace(instanceId),
    enabled: instanceId !== '0' && instanceId !== ''
  });

  const caps = useQuery({
    queryKey: ['approval', 'capabilities', instanceId],
    queryFn: () => hq.approval.capabilities(instanceId),
    enabled: instanceId !== '0' && instanceId !== ''
  });

  const users = useQuery({
    queryKey: ['approval', 'user-search', userKeyword],
    queryFn: () => hq.approval.searchUsers(userKeyword || undefined),
    enabled: transferOpen || signOpen
  });

  const userOptions = (users.data ?? []).map((item) => ({
    value: item.id,
    label: `${item.name ?? item.id}${item.userCode ? `（${item.userCode}）` : ''}`
  }));

  const fail = (error: unknown, fallback: string) => {
    if (error instanceof ApiError) return message.error(error.friendlyMessage);
    if (error instanceof Error && error.message) return message.error(error.message);
    return message.error(fallback);
  };

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['approval'] });
    await queryClient.invalidateQueries({ queryKey: ['apply'] });
    await onChanged?.();
  };

  const remind = useMutation({
    mutationFn: () => hq.approval.remind(instanceId),
    onSuccess: async (data) => {
      message.success(`已催办 ${data.reminded} 位审批人`);
      await refresh();
    },
    onError: (error) => fail(error, '催办失败')
  });

  const transfer = useMutation({
    mutationFn: (values: { toUserId: string; opinion?: string }) => {
      const taskId = caps.data?.myPendingTaskId;
      if (!taskId) return Promise.reject(new Error('没有待你处理的审批任务'));
      return hq.approval.transfer(taskId, { toUserId: values.toUserId, opinion: values.opinion });
    },
    onSuccess: async () => {
      message.success('已转办');
      setTransferOpen(false);
      transferForm.resetFields();
      await refresh();
    },
    onError: (error) => fail(error, '转办失败')
  });

  const addSigner = useMutation({
    mutationFn: (values: { userId: string; opinion?: string }) =>
      hq.approval.addSigner(instanceId, { userId: values.userId, opinion: values.opinion }),
    onSuccess: async () => {
      message.success('已加签');
      setSignOpen(false);
      signForm.resetFields();
      await refresh();
    },
    onError: (error) => fail(error, '加签失败')
  });

  const rejectTo = useMutation({
    mutationFn: (values: { targetNodeCode: string; opinion?: string }) =>
      hq.approval.rejectTo(instanceId, values),
    onSuccess: async (data) => {
      message.success(`已退回至「${data.nodeName ?? data.nodeCode ?? '目标节点'}」`);
      setRejectOpen(false);
      rejectForm.resetFields();
      await refresh();
    },
    onError: (error) => fail(error, '退回失败')
  });

  const terminate = useMutation({
    mutationFn: (reason?: string) => hq.approval.terminate(instanceId, reason),
    onSuccess: async () => {
      message.success('审批已终止');
      setTerminateOpen(false);
      terminateForm.resetFields();
      await refresh();
    },
    onError: (error) => fail(error, '终止失败')
  });

  const taskColumns: ColumnsType<ApprovalTraceTask> = [
    { title: '节点', dataIndex: 'nodeName', width: 160, render: (value: string | null, row) => value || row.nodeCode },
    {
      title: '处理人',
      dataIndex: 'assigneeName',
      width: 150,
      render: (value: string | null, row) =>
        value ? (
          <Space size={4}>
            {value}
            {row.isDelegated ? <Tag color="purple">代批</Tag> : null}
          </Space>
        ) : (
          row.assigneeUserId
        )
    },
    {
      title: '状态',
      dataIndex: 'taskStatus',
      width: 110,
      render: (value: string) => {
        const meta = STATUS_META[value] ?? { color: 'default', text: value };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    { title: '动作', dataIndex: 'action', width: 110, render: (value: string | null) => (value ? ACTION_TEXT[value] ?? value : '-') },
    { title: '意见', dataIndex: 'opinion', ellipsis: { showTitle: false }, render: ellipsisCell('opinion') },
    { title: '催办', dataIndex: 'remindCount', width: 80, render: (value: number) => (value > 0 ? `${value} 次` : '-') },
    { title: '处理时间', dataIndex: 'handleAt', width: 160, render: (value: string | null) => formatDisplayTime(value) },
    { title: '应处理', dataIndex: 'dueAt', width: 160, render: (value: string | null) => formatDisplayTime(value) }
  ];

  const logColumns: ColumnsType<ApprovalTraceLog> = [
    { title: '动作', dataIndex: 'action', width: 120, render: (value: string) => ACTION_TEXT[value] ?? value },
    { title: '节点', dataIndex: 'nodeCode', width: 120, render: (value: string | null) => value || '-' },
    {
      title: '状态变更',
      key: 'status',
      width: 180,
      render: (_value: unknown, row) => `${statusText(row.fromStatus)} → ${statusText(row.toStatus)}`
    },
    { title: '操作人', dataIndex: 'actorName', width: 150, render: (value: string | null, row) => value || row.actorUserId },
    {
      title: '等级',
      dataIndex: 'actorLevel',
      width: 100,
      render: (value: string | null) => (value ? LEVEL_LABELS[value] ?? value : '-')
    },
    { title: '意见', dataIndex: 'opinion', ellipsis: { showTitle: false }, render: ellipsisCell('opinion') },
    { title: '时间', dataIndex: 'actAt', width: 160, render: (value: string) => formatDisplayTime(value) }
  ];

  const capabilities = caps.data;
  const instance = trace.data?.instance;
  const tasks = trace.data?.tasks ?? [];
  const logs = trace.data?.logs ?? [];

  if (instanceId === '0' || instanceId === '') {
    return (
      <Card className="page-card" title="审批流转">
        <Typography.Text type="secondary">该单据尚未提交审批，暂无流转记录。</Typography.Text>
      </Card>
    );
  }

  return (
    <Card
      className="page-card"
      title="审批流转"
      loading={(instanceId !== '0' && instanceId !== '') && trace.isPending}
      extra={
        <Space wrap>
          {capabilities?.canRemind ? (
            <Button loading={remind.isPending} onClick={() => remind.mutate()}>
              催办
            </Button>
          ) : null}
          {capabilities?.canTransfer ? (
            <Button onClick={() => setTransferOpen(true)}>转办</Button>
          ) : null}
          {capabilities?.canAddSigner ? <Button onClick={() => setSignOpen(true)}>加签</Button> : null}
          {capabilities?.canRejectTo && (capabilities.rejectTargets?.length ?? 0) > 0 ? (
            <Button onClick={() => setRejectOpen(true)}>退回</Button>
          ) : null}
          {capabilities?.canTerminate ? (
            <Button danger onClick={() => setTerminateOpen(true)}>
              终止
            </Button>
          ) : null}
          <Button onClick={() => void refresh()}>刷新</Button>
        </Space>
      }
    >
      {trace.isError ? (
        <Alert
          type="error"
          showIcon
          message="审批轨迹加载失败"
          description={trace.error instanceof ApiError ? trace.error.friendlyMessage : '可能无查看授权'}
        />
      ) : instance ? (
        <>
          <Descriptions size="small" column={3} bordered style={{ marginBottom: 16 }}>
            <Descriptions.Item label="审批状态">
              <Tag color={instance.instanceStatus === 'APPROVED' ? 'green' : instance.instanceStatus === 'RUNNING' ? 'processing' : 'default'}>
                {instance.instanceStatus}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="当前节点">{instance.currentNodeName || instance.currentNodeCode || '-'}</Descriptions.Item>
            <Descriptions.Item label="流程版本">v{instance.flowVersion}</Descriptions.Item>
            <Descriptions.Item label="发起人">{instance.startUserName || instance.startUserId}</Descriptions.Item>
            <Descriptions.Item label="发起时间">{formatDisplayTime(instance.startAt)}</Descriptions.Item>
            <Descriptions.Item label="结束时间">{formatDisplayTime(instance.endAt)}</Descriptions.Item>
            <Descriptions.Item label="耗时">{instance.durationMinutes > 0 ? `${instance.durationMinutes} 分钟` : '-'}</Descriptions.Item>
            <Descriptions.Item label="驳回/终止原因" span={2}>
              {instance.rejectReason || '-'}
            </Descriptions.Item>
          </Descriptions>

          <Tabs
            items={[
              {
                key: 'tasks',
                label: `审批任务（${tasks.length}）`,
                children: (
                  <Table<ApprovalTraceTask>
                    rowKey="taskId"
                    size="small"
                    columns={taskColumns}
                    dataSource={tasks}
                    pagination={false}
                    scroll={{ x: sumWidth(taskColumns) }}
                    expandable={{
                      rowExpandable: (row) => row.taskStatus === 'PENDING',
                      expandedRowRender: (row) => <PendingTaskActions row={row} />
                    }}
                  />
                )
              },
              {
                key: 'logs',
                label: `流转日志（${logs.length}）`,
                children: (
                  <Table<ApprovalTraceLog>
                    rowKey="logId"
                    size="small"
                    columns={logColumns}
                    dataSource={logs}
                    pagination={false}
                    scroll={{ x: sumWidth(logColumns) }}
                  />
                )
              }
            ]}
          />
        </>
      ) : (
        <Typography.Text type="secondary">暂无流转记录</Typography.Text>
      )}

      <Modal
        title="转办审批任务"
        open={transferOpen}
        onCancel={() => setTransferOpen(false)}
        destroyOnHidden
        onOk={() => void transferForm.submit()}
        confirmLoading={transfer.isPending}
      >
        <Form form={transferForm} layout="vertical" preserve={false} onFinish={(values) => transfer.mutate(values)}>
          <Form.Item name="toUserId" label="转办给" rules={[{ required: true, message: '请选择接收人' }]}>
            <Select
              showSearch
              placeholder="输入姓名 / 工号搜索"
              filterOption={false}
              onSearch={(value) => setUserKeyword(value)}
              loading={users.isFetching}
              options={userOptions}
            />
          </Form.Item>
          <Form.Item name="opinion" label="转办说明">
            <Input.TextArea rows={2} maxLength={500} showCount />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="加签"
        open={signOpen}
        onCancel={() => setSignOpen(false)}
        destroyOnHidden
        onOk={() => void signForm.submit()}
        confirmLoading={addSigner.isPending}
      >
        <Form form={signForm} layout="vertical" preserve={false} onFinish={(values) => addSigner.mutate(values)}>
          <Typography.Paragraph type="secondary">
            在当前审批节点追加一名审批人；会签节点会同步影响通过比例（sign_rate）。
          </Typography.Paragraph>
          <Form.Item name="userId" label="加签人" rules={[{ required: true, message: '请选择加签人' }]}>
            <Select
              showSearch
              placeholder="输入姓名 / 工号搜索"
              filterOption={false}
              onSearch={(value) => setUserKeyword(value)}
              loading={users.isFetching}
              options={userOptions}
            />
          </Form.Item>
          <Form.Item name="opinion" label="加签说明">
            <Input.TextArea rows={2} maxLength={500} showCount />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="退回指定节点"
        open={rejectOpen}
        onCancel={() => setRejectOpen(false)}
        destroyOnHidden
        onOk={() => void rejectForm.submit()}
        confirmLoading={rejectTo.isPending}
      >
        <Form form={rejectForm} layout="vertical" preserve={false} onFinish={(values) => rejectTo.mutate(values)}>
          <Form.Item name="targetNodeCode" label="退回节点" rules={[{ required: true, message: '请选择退回节点' }]}>
            <Select
              placeholder="仅可退回当前节点之前的审批节点"
              options={(capabilities?.rejectTargets ?? []).map((item) => ({
                value: item.nodeCode,
                label: `${item.nodeName}（${item.nodeCode}）`
              }))}
            />
          </Form.Item>
          <Form.Item name="opinion" label="退回意见" rules={[{ required: true, message: '退回必须说明原因' }]}>
            <Input.TextArea rows={3} maxLength={500} showCount />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="终止审批"
        open={terminateOpen}
        onCancel={() => setTerminateOpen(false)}
        destroyOnHidden
        okText="确认终止"
        okButtonProps={{ danger: true }}
        confirmLoading={terminate.isPending}
        onOk={() => void terminateForm.submit()}
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="终止后所有待办将跳过，业务单据置为已取消，且不可恢复。"
        />
        <Form form={terminateForm} layout="vertical" preserve={false} onFinish={(values) => terminate.mutate(values.reason)}>
          <Form.Item name="reason" label="终止原因" rules={[{ required: true, message: '请填写终止原因' }]}>
            <Input.TextArea rows={3} maxLength={500} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}

/** 待办行的快捷操作说明（处理动作仍在业务详情页，此处仅提示）。 */
function PendingTaskActions({ row }: { row: ApprovalTraceTask }) {
  return (
    <Typography.Text type="secondary">
      {row.nodeName ?? row.nodeCode} · 待 {row.assigneeName ?? row.assigneeUserId} 处理
      {row.dueAt ? `（应于 ${formatDisplayTime(row.dueAt)} 前完成）` : ''}
    </Typography.Text>
  );
}
